import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../api';
import { useUserProfile } from './useUserProfile';

/**
 * AV Redaction Studio state — redaction marks on one media document.
 *
 * Owns the spec rows (team-shared, loaded from the flag-gated backend),
 * the studio mode toggle, and the LIVE PREVIEW engine: while redaction
 * mode + preview are on, playback is gated against the marked ranges —
 * the player mutes inside every range and a Web Audio 1 kHz tone plays
 * over bleep ranges. No render needed to hear the result.
 *
 * Feature-gated by /auth/me `features.av_redaction` (off → `enabled`
 * false and the studio UI renders nothing). The kind default is
 * length-based and server-aligned: ranges ≤ bleepMaxSeconds default to
 * bleep, longer to mute. See plans/AV_REDACTION_PLAN.md.
 */
export default function useRedactions({ caseId, documentId, mediaRef }) {
  const { profile } = useUserProfile();
  const enabled = !!profile?.features?.av_redaction;
  const bleepMaxSeconds = profile?.features?.av_redaction_bleep_max_seconds ?? 10;

  const [mode, setMode] = useState(false);
  const [preview, setPreview] = useState(true);
  // Draw-on-frame mode (video only): null | {style: 'blur'|'black'}.
  const [drawMode, setDrawMode] = useState(null);
  // Last successfully created mark — drives the "✓ added" confirmation
  // (a draw that captures nothing must be visibly different from one
  // that worked; the missing-blackout report proved silence is read as
  // success).
  const [lastAdded, setLastAdded] = useState(null);
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [duration, setDuration] = useState(0);

  const base = `/cases/${caseId}/documents/${documentId}/redactions`;

  // ── Load team-shared marks once the feature is confirmed on ────────
  useEffect(() => {
    if (!enabled || !caseId || !documentId) return;
    let cancelled = false;
    api.get(base)
      .then(d => { if (!cancelled) { setItems(d.redactions || []); setLoaded(true); } })
      .catch(e => { if (!cancelled) { setError(e.message); setLoaded(true); } });
    return () => { cancelled = true; };
  }, [enabled, caseId, documentId]);  // eslint-disable-line react-hooks/exhaustive-deps

  // ── Track media duration for the timeline strip ─────────────────────
  useEffect(() => {
    const el = mediaRef.current;
    if (!el || !enabled) return;
    const apply = () => { if (Number.isFinite(el.duration)) setDuration(el.duration); };
    apply();
    el.addEventListener('loadedmetadata', apply);
    el.addEventListener('durationchange', apply);
    return () => {
      el.removeEventListener('loadedmetadata', apply);
      el.removeEventListener('durationchange', apply);
    };
  }, [mediaRef, enabled, mode]);

  const defaultKindFor = useCallback(
    (rangeSeconds) => (rangeSeconds <= bleepMaxSeconds ? 'audio_bleep' : 'audio_mute'),
    [bleepMaxSeconds],
  );

  const addRange = useCallback(async (start, end, label) => {
    const s = Math.max(0, Math.min(start, end));
    const e = Math.max(start, end);
    if (e - s < 0.05) return null;
    setError(null);
    try {
      const row = await api.post(base, {
        kind: defaultKindFor(e - s),
        time_start: s,
        time_end: e,
        label: label ? String(label).slice(0, 300) : null,
      });
      setItems(prev =>
        [...prev, row].sort((a, b) => a.time_start - b.time_start));
      setLastAdded(row);
      return row;
    } catch (err) {
      setError(err.message);
      return null;
    }
  }, [base, defaultKindFor]);

  // Default span for frame-anchored marks: ~5s bracketing the playhead;
  // the waveform/list fine-tunes from there.
  const _frameSpan = useCallback(() => {
    const el = mediaRef.current;
    const t = el ? el.currentTime : 0;
    const dur = (el && Number.isFinite(el.duration)) ? el.duration : null;
    let start = Math.max(0, t - 0.5);
    let end = start + 5;
    if (dur) {
      end = Math.min(end, dur);
      start = Math.max(0, Math.min(start, end - 0.5));
    }
    return [start, end];
  }, [mediaRef]);

  const addRegion = useCallback(async (bbox, style) => {
    const [start, end] = _frameSpan();
    setError(null);
    setDrawMode(null);
    try {
      const row = await api.post(base, {
        kind: 'video_region',
        time_start: start,
        time_end: end,
        region: { bbox, style },
      });
      setItems(prev =>
        [...prev, row].sort((a, b) => a.time_start - b.time_start));
      setLastAdded(row);
      return row;
    } catch (err) {
      setError(err.message);
      return null;
    }
  }, [base, _frameSpan]);

  const addFrameBlackout = useCallback(async () => {
    const [start, end] = _frameSpan();
    setError(null);
    try {
      const row = await api.post(base, {
        kind: 'video_frame',
        time_start: start,
        time_end: end,
      });
      setItems(prev =>
        [...prev, row].sort((a, b) => a.time_start - b.time_start));
      setLastAdded(row);
      return row;
    } catch (err) {
      setError(err.message);
      return null;
    }
  }, [base, _frameSpan]);

  const updateItem = useCallback(async (id, patch) => {
    setError(null);
    try {
      const row = await api.patch(`${base}/${id}`, patch);
      setItems(prev =>
        prev.map(r => (r.id === id ? row : r)).sort((a, b) => a.time_start - b.time_start));
      return row;
    } catch (err) {
      setError(err.message);
      return null;
    }
  }, [base]);

  const removeItem = useCallback(async (id) => {
    setError(null);
    try {
      await api.del(`${base}/${id}`);
      setItems(prev => prev.filter(r => r.id !== id));
    } catch (err) {
      setError(err.message);
    }
  }, [base]);

  // ── Live preview engine ──────────────────────────────────────────────
  // Poll playback position at 60ms (timeupdate alone is ~4 Hz — too
  // coarse for range edges): mute inside any range, tone over bleeps.
  //
  // The tone graph must be created/resumed inside a USER GESTURE —
  // browsers (Safari hardest) keep an AudioContext born in a timer
  // silently suspended forever. unlockTone() runs in the Redact /
  // Preview toggle click handlers; the polling loop only moves gain.
  const audioCtxRef = useRef(null);
  const gainRef = useRef(null);

  const unlockTone = useCallback(() => {
    try {
      if (!audioCtxRef.current) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        const ctx = new Ctx();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        gain.gain.value = 0;
        osc.frequency.value = 1000;
        osc.connect(gain).connect(ctx.destination);
        osc.start();
        audioCtxRef.current = ctx;
        gainRef.current = gain;
      }
      if (audioCtxRef.current.state === 'suspended') {
        audioCtxRef.current.resume().catch(() => {});
      }
    } catch { /* tone preview unavailable — mute gating still applies */ }
  }, []);

  // Close the audio graph on unmount only (not on item edits — closing
  // mid-session would re-suspend the context outside a gesture).
  useEffect(() => () => {
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch { /* already closed */ }
      audioCtxRef.current = null;
      gainRef.current = null;
    }
  }, []);

  const setModeUnlocked = useCallback((on) => {
    if (on) unlockTone();
    setMode(on);
  }, [unlockTone]);

  const setPreviewUnlocked = useCallback((on) => {
    if (on) unlockTone();
    setPreview(on);
  }, [unlockTone]);

  useEffect(() => {
    const el = mediaRef.current;
    if (!el || !mode || !preview || items.length === 0) return;

    // Only active marks gate playback (the render path filters the same way).
    const live = items.filter(r => r.status === 'active');
    const id = setInterval(() => {
      const t = el.currentTime;
      const playing = !el.paused && !el.ended;
      const inAny = playing && live.some(r =>
        (r.kind === 'audio_mute' || r.kind === 'audio_bleep')
        && t >= r.time_start && t < r.time_end);
      const inBleep = playing && live.some(
        r => r.kind === 'audio_bleep' && t >= r.time_start && t < r.time_end);
      el.muted = inAny;
      if (gainRef.current) {
        gainRef.current.gain.value = inBleep ? 0.12 : 0;
        if (inBleep && audioCtxRef.current?.state === 'suspended') {
          audioCtxRef.current.resume().catch(() => {});
        }
      }
    }, 60);

    return () => {
      clearInterval(id);
      el.muted = false;
      if (gainRef.current) gainRef.current.gain.value = 0;
    };
  }, [mediaRef, mode, preview, items]);

  return {
    enabled,
    loaded,
    error,
    mode,
    setMode: setModeUnlocked,
    preview,
    setPreview: setPreviewUnlocked,
    items,
    duration,
    // Accessor (not the element itself) so consumers re-read after the
    // player remounts on source changes.
    mediaEl: () => mediaRef.current,
    bleepMaxSeconds,
    defaultKindFor,
    addRange,
    addRegion,
    addFrameBlackout,
    drawMode,
    setDrawMode,
    lastAdded,
    updateItem,
    removeItem,
    activeItems: items.filter(r => r.status === 'active'),
  };
}
